import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { openDB } from "idb";
import Sidebar from "../../components/Dashboard/Sidebar";
import Header from "../../components/Dashboard/Header";
import Footer from "../../components/Dashboard/Footer";
import Barchart from "../../components/Dashboard/Charts/Barchart";
import Piechart from "../../components/Dashboard/Charts/Piechart";
import Table from "../../components/Dashboard/Charts/Table";
import TreeMapChart from "../../components/Dashboard/Charts/Treemap";
import StatCards from "../../components/Dashboard/Charts/Statecards";
import DonutChart from "../../components/Dashboard/Charts/DonutChart";
import SmellTypeTabs from "../../components/Dashboard/Visualize/SmellTypeTabs";
import WorldCup from "../../components/Dashboard/Charts/worldcup";
import LoadingSpinner from "./LoadingSpinner";
import TrendChart from "../../components/Dashboard/Charts/TrendChart";
import { Copy } from 'react-bootstrap-icons';

// Efficient Commit Search Component
const EfficientCommitSearch = ({
  projectId,
  onCommitSelect,
  selectedCommitId,
  disabled = false
}) => {
  const [searchQuery, setSearchQuery] = useState(selectedCommitId || '');
  const [searchResults, setSearchResults] = useState([]);
  const [recentCommits, setRecentCommits] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({});
  const [commitStats, setCommitStats] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const performSearch = useCallback(async (query, page = 1) => {
    if (!projectId) return;

    try {
      setLoading(true);

      const params = {
        search: query.trim(),
        page: page,
        page_size: 20,
        sort: 'desc'
      };

      if (!params.search) {
        delete params.search;
      }

      const response = await axios.get(
        `${BACKEND_URL}/projects/${projectId}/commits/search/`,
        { params }
      );

      setSearchResults(response.data.commits || []);
      setPagination(response.data.pagination || {});
      setCurrentPage(page);

    } catch (error) {
      console.error('Error searching commits:', error);
      setSearchResults([]);
      setPagination({});
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const handleSearchInput = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value, 1);
    }, 300);
  };

  useEffect(() => {
    const loadRecentCommits = async () => {
      if (!projectId) return;

      try {
        const response = await axios.get(
          `${BACKEND_URL}/projects/${projectId}/commits/recent/`,
          { params: { limit: 10 } }
        );
        setRecentCommits(response.data.recent_commits || []);
      } catch (error) {
        console.error('Error loading recent commits:', error);
      }
    };

    const loadCommitStats = async () => {
      if (!projectId) return;

      try {
        const response = await axios.get(
          `${BACKEND_URL}/projects/${projectId}/commits/stats/`
        );
        setCommitStats(response.data);
      } catch (error) {
        console.error('Error loading commit stats:', error);
      }
    };

    loadRecentCommits();
    loadCommitStats();
  }, [projectId]);

  const handleCommitSelect = (commitId) => {
    setSearchQuery(commitId);
    setShowDropdown(false);
    onCommitSelect(commitId);
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
    if (!searchQuery.trim()) {
      setSearchResults(recentCommits);
      setPagination({});
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setShowDropdown(false);
    }, 150);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (pagination.total_pages || 1)) {
      performSearch(searchQuery, newPage);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setPagination({});
    setCurrentPage(1);
    onCommitSelect('');
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const formatCommitId = (commitId) => {
    return commitId ? `${commitId.substring(0, 8)}...` : '';
  };

  const displayResults = searchQuery.trim() ? searchResults : recentCommits;
  const isRecentMode = !searchQuery.trim();

  return (
    <div className="position-relative" style={{ maxWidth: "400px", width: "100%" }}>
      {commitStats && (
        <div className="mb-2">
          <small className="text-muted">
            {commitStats.total_commits.toLocaleString()} total commits
            {commitStats.latest_commit && (
              <span> • Latest: {formatCommitId(commitStats.latest_commit.commit_id)}</span>
            )}
          </small>
        </div>
      )}

      <div className="input-group">
        <input
          ref={searchInputRef}
          type="text"
          className="form-control"
          placeholder="Search commit ID... (leave empty for recent)"
          value={searchQuery}
          onChange={(e) => handleSearchInput(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          disabled={disabled}
          style={{ fontFamily: 'monospace', fontSize: '0.9em' }}
        />

        {searchQuery && (
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={clearSearch}
            disabled={disabled}
            title="Clear search"
          >
            ✕
          </button>
        )}

        {loading && (
          <span className="input-group-text">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </span>
        )}
      </div>

      {showDropdown && (
        <div
          ref={dropdownRef}
          className="position-absolute w-100 mt-1 bg-white border rounded shadow-lg"
          style={{ zIndex: 1050, maxHeight: '400px', overflowY: 'auto' }}
        >
          <div className="px-3 py-2 bg-light border-bottom">
            <small className="text-muted fw-bold">
              {isRecentMode ? 'Recent Commits' : `Search Results (${pagination.total_count || 0})`}
            </small>
          </div>

          {displayResults.length > 0 ? (
            <>
              {displayResults.map((commit, index) => (
                <div
                  key={`${commit.commit_id}-${index}`}
                  className={`px-3 py-2 border-bottom commit-item ${commit.commit_id === selectedCommitId ? 'bg-primary text-white' : ''
                    }`}
                  style={{ cursor: 'pointer' }}
                  onMouseDown={() => handleCommitSelect(commit.commit_id)}
                  onMouseEnter={(e) => {
                    if (commit.commit_id !== selectedCommitId) {
                      e.target.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (commit.commit_id !== selectedCommitId) {
                      e.target.style.backgroundColor = 'white';
                    }
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <code className="small">{commit.commit_id}</code>
                      {commit.created_at && (
                        <div className="text-muted small mt-1">
                          {new Date(commit.created_at).toLocaleDateString()} at{' '}
                          {new Date(commit.created_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {!isRecentMode && pagination.total_pages > 1 && (
                <div className="px-3 py-2 bg-light border-top">
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      Page {pagination.current_page} of {pagination.total_pages}
                    </small>
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={!pagination.has_previous || loading}
                      >
                        ‹
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={!pagination.has_next || loading}
                      >
                        ›
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="px-3 py-3 text-center text-muted">
              {loading ? (
                <div>
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  Searching commits...
                </div>
              ) : isRecentMode ? (
                'No recent commits found'
              ) : searchQuery.trim() ? (
                `No commits found matching "${searchQuery}"`
              ) : (
                'Start typing to search commits'
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function VisualizeReport() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeSidebarItem, setActiveSidebarItem] = useState(1);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedType, setSelectedType] = useState("all");
  const [filteredBackendData, setFilteredBackendData] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [pieChartLoading, setPieChartLoading] = useState(true);
  const [barChartLoading, setBarChartLoading] = useState(true);
  const [topSmellTypesLoading, setTopSmellTypesLoading] = useState(true);
  const [implementationBarChartLoading, setImplementationBarChartLoading] = useState(true);

  const [backendData, setBackendData] = useState(null);
  const [selectedTool, setSelectedTool] = useState("");
  const [projectId, setProjectId] = useState("");

  // Optimized commit-related states
  const [selectedCommitId, setSelectedCommitId] = useState("");
  const [loadingCommitData, setLoadingCommitData] = useState(false);

  const { smellType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedSmellTypeFilter = smellType || "all";

  // Initialize IndexedDB
  const dbPromise = openDB("AnalysisDB", 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains("analysis")) {
        db.createObjectStore("analysis", { keyPath: "id" });
      }
    },
  });
  const commit_id_local = localStorage.getItem("commit_id");

  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  // Function to copy commit ID to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setShowCopyTooltip(true);
        setTimeout(() => setShowCopyTooltip(false), 2000); // Hide tooltip after 2 seconds
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
        setShowCopyTooltip(false);
      });
  };

  // Initialize from navigation state
  useEffect(() => {
    if (location.state?.selectedCommitId) {
      setSelectedCommitId(location.state.selectedCommitId);
    }
    if (location.state?.projectId) {
      setProjectId(location.state.projectId);
    }
  }, [location.state]);

  const projectName = localStorage.getItem("selectedProjectName");

  // Fetch initial data
  useEffect(() => {
    async function fetchData() {
      try {
        const db = await dbPromise;

        const storedData = await db.get("analysis", "analyzedData");
        const storedTool = await db.get("analysis", "selectedTool");
        const storedProjectId = await db.get("analysis", "projectId");

        if (storedData && storedData.data) {
          setBackendData(storedData.data);
          setFilteredBackendData(storedData.data);
          setSelectedTool(storedTool?.data || "");

          if (!projectId && storedProjectId?.data) {
            setProjectId(storedProjectId.data);
          }
        } else {
          console.error("No analyzed data found in IndexedDB. Redirecting...");
          navigate("/upload");
        }
      } catch (err) {
        console.error("Failed to retrieve data:", err);
        navigate("/upload");
      }
    }

    fetchData();
  }, [navigate, projectId]);

  // Handle commit selection
  const handleCommitSelect = async (commitId) => {
    if (!commitId || !projectId) {
      console.error("Missing commit ID or project ID");
      return;
    }

    setSelectedCommitId(commitId);
    await fetchDataForCommit(commitId);
  };

  // Fetch data for specific commit
  const fetchDataForCommit = async (commitId) => {
    if (!commitId || !projectId) {
      console.error("Missing commit ID or project ID for fetching data");
      return;
    }

    setLoadingCommitData(true);

    try {

      const response = await axios.get(
        `${BACKEND_URL}/projects/${projectId}/${commitId}/visualize/`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 30000
        }
      );

      if (response.data && response.data.data) {
        const newData = response.data.data;
        setBackendData(newData);
        setFilteredBackendData(newData);

        try {
          const db = await dbPromise;
          await db.put('analysis', {
            id: 'analyzedData',
            data: newData
          });

          await db.put('analysis', {
            id: 'currentCommitId',
            data: commitId
          });


        } catch (dbError) {
          console.warn("Failed to update IndexedDB:", dbError);
        }

        setPieChartLoading(false);
        setBarChartLoading(false);
        setTopSmellTypesLoading(false);
        setImplementationBarChartLoading(false);

      } else {
        console.error("Invalid response structure:", response.data);
        alert(`Invalid data received for commit ${commitId}. Please try again.`);
      }

    } catch (error) {
      console.error("Error fetching data for commit:", error);

      let errorMessage = `Failed to fetch data for commit ${commitId}.`;

      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        if (status === 404) {
          errorMessage += " Commit not found or no data available.";
        } else if (status === 401) {
          errorMessage += " Unauthorized access.";
        } else if (status === 500) {
          errorMessage += " Server error occurred.";
        } else {
          errorMessage += ` Server returned ${status}: ${data?.error || data?.message || 'Unknown error'}`;
        }
      } else if (error.request) {
        errorMessage += " Network error - please check your connection.";
      } else {
        errorMessage += ` Error: ${error.message}`;
      }

      alert(errorMessage);
    } finally {
      setLoadingCommitData(false);
    }
  };

  // Clear commit selection
  const clearCommitSelection = async () => {
    setSelectedCommitId('');

    try {
      const db = await dbPromise;
      const originalData = await db.get("analysis", "originalAnalyzedData");

      if (originalData?.data) {
        setBackendData(originalData.data);
        setFilteredBackendData(originalData.data);
      }
    } catch (error) {
      console.error("Error restoring original data:", error);
    }
  };

  // Store original data when component first loads
  useEffect(() => {
    const storeOriginalData = async () => {
      if (backendData && !selectedCommitId) {
        try {
          const db = await dbPromise;
          await db.put('analysis', {
            id: 'originalAnalyzedData',
            data: backendData
          });
        } catch (error) {
          console.error("Error storing original data:", error);
        }
      }
    };

    storeOriginalData();
  }, [backendData, selectedCommitId]);

  // Reusable No Data Available component
  const NoDataAvailable = ({ title }) => (
    <div className="card p-3 shadow-sm text-center">
      <h5>{title}</h5>
      <p className="text-muted">
        No data available. Please see your uploaded files.
      </p>
    </div>
  );

  const handleTypeChange = (type) => {
    setSelectedType(type);
  };

  useEffect(() => {
    document.title = "DCode | Code quality report";
  }, []);

  // Data extraction
  const pieChartData = filteredBackendData?.pieChartData || [];
  const topSmellsData = filteredBackendData?.topFilesBySmellType || [];
  const topSmellTypes = filteredBackendData?.topSmellTypes || [];
  const architectureDonutData = filteredBackendData?.architectureDonutData || [];
  const architectureTableData = filteredBackendData?.architectureTableData || [];
  const architectureTreeMapData = filteredBackendData?.architectureTreeMapData || [];
  const architectureBarData = filteredBackendData?.architectureBarData || [];
  const implementationTopSmells = filteredBackendData?.implementationTopSmells || [];
  const implementationPieChart = filteredBackendData?.implementationPieChart || [];
  const implementationTableData = filteredBackendData?.implementationTableData || [];
  const implementationTreeMapData = filteredBackendData?.implementationTreeMapData || [];
  const designTreeMapData = filteredBackendData?.designTreeMapData || [];
  const designBarChartData = filteredBackendData?.designBarChartData || [];
  const designPieChartData = filteredBackendData?.designPieChartData || [];
  const analysisSummary = filteredBackendData?.analysisSummary || [];
  const designTableData = filteredBackendData?.designTableData || [];
  const testabilityPieChartData = filteredBackendData?.testabilityPieChartData || [];
  const testPieChartData = filteredBackendData?.testPieChartData || [];
  const testabilityTableData = filteredBackendData?.testabilityTableData || [];
  const testabilityTreeMapData = filteredBackendData?.testabilityTreeMapData || [];
  const testTableData = filteredBackendData?.testTableData || [];
  const testabilityBarChartData = filteredBackendData?.testabilityBarChartData || [];
  const testBarChartData = filteredBackendData?.testBarChartData || [];
  const testTreeMapData = filteredBackendData?.testTreeMapData || [];
  const graphData = filteredBackendData?.graphML || null;

  const isLargeScreen = windowWidth >= 992;

  const filteredData = React.useMemo(() => {
    if (!topSmellsData || !selectedType) return [];

    const currentTypeData = topSmellsData[selectedType] || [];

    return currentTypeData.map(({ file, count }) => ({
      smell: file,
      count,
    }));
  }, [selectedType, topSmellsData]);



  const arcTableColumns = [
    { label: "Smell", key: "smell" },
    { label: "Package", key: "package" },
    { label: "Details", key: "description" },
  ];

  const impTableColumns = [
    { label: "Smell", key: "smell" },
    { label: "Package", key: "package" },
    { label: "Class", key: "class" },
    { label: "Function", key: "function" },
    { label: "Line No", key: "line" },
    { label: "Details", key: "details" },
  ];

  const designTableColumns = [
    { label: "Smell", key: "smell" },
    { label: "Package", key: "package" },
    { label: "Class", key: "class" },
    { label: "Line No", key: "line" },
    { label: "Details", key: "details" },
  ];

  const testabilityTableColumns = [
    { label: "Smell", key: "smell" },
    { label: "Package", key: "package" },
    { label: "Details", key: "details" },
  ];

  const testTableColumns = [
    { label: "Smell", key: "smell" },
    { label: "Package", key: "package" },
    { label: "Details", key: "details" },
  ];

  const getSidebarWidth = () => {
    if (!sidebarOpen) return "0px";
    return sidebarCollapsed ? "80px" : "280px";
  };

  const handleSidebarItemClick = (index) => {
    setActiveSidebarItem(index);
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setSidebarOpen(window.innerWidth >= 992);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleUserDropdown = () => setUserDropdownOpen(!userDropdownOpen);

  const handleSidebarCollapseChange = (collapsed) => {
    setSidebarCollapsed(collapsed);
  };

  // Loading effects
  useEffect(() => {
    if (pieChartData && pieChartData.length > 0) {
      setPieChartLoading(true);
      const timeout = setTimeout(() => {
        setPieChartLoading(false);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [pieChartData]);

  useEffect(() => {
    const currentTypeData = topSmellsData?.[selectedType] || [];
    if (currentTypeData.length > 0) {
      setBarChartLoading(true);
      const timeout = setTimeout(() => {
        setBarChartLoading(false);
      }, 500);
      return () => clearTimeout(timeout);
    } else {
      setBarChartLoading(false);
    }
  }, [topSmellsData, selectedType]);

  useEffect(() => {
    if (topSmellTypes.length > 0) {
      setTopSmellTypesLoading(true);
      const timeout = setTimeout(() => {
        setTopSmellTypesLoading(false);
      }, 500);
      return () => clearTimeout(timeout);
    } else {
      setTopSmellTypesLoading(false);
    }
  }, [topSmellTypes]);

  useEffect(() => {
    if (implementationTopSmells && implementationTopSmells.length > 0) {
      setImplementationBarChartLoading(true);
      const timeout = setTimeout(() => {
        setImplementationBarChartLoading(false);
      }, 500);
      return () => clearTimeout(timeout);
    } else {
      setImplementationBarChartLoading(false);
    }
  }, [implementationTopSmells]);

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Header with dynamic left margin */}
      <div
        style={{
          marginLeft: isLargeScreen ? getSidebarWidth() : "0px",
          transition: "margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          zIndex: 1030,
        }}
      >
        <Header
          sidebarOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
          isCollapsed={sidebarCollapsed}
          userDropdownOpen={userDropdownOpen}
          toggleUserDropdown={toggleUserDropdown}
          pageTitle="Code quality report"
        />
      </div>

      {/* Sidebar with collapse callback */}
      <Sidebar
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        activeItem={activeSidebarItem}
        onItemClick={handleSidebarItemClick}
        onCollapseChange={handleSidebarCollapseChange}
      />

      {/* Mobile overlay */}
      {sidebarOpen && !isLargeScreen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1040 }}
          onClick={toggleSidebar}
        ></div>
      )}

      {/* Loading overlay for commit data */}
      {loadingCommitData && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ backgroundColor: "rgba(0,0,0,0.7)", zIndex: 2000 }}
        >
          <LoadingSpinner title="Loading commit data..." />
        </div>
      )}

      {/* Main content with dynamic margin */}
      <main
        style={{
          marginLeft: isLargeScreen ? getSidebarWidth() : "0px",
          paddingTop: "70px",
          transition: "margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "calc(100vh - 140px)",
        }}
      >
        <div className="container-fluid px-4">


          <div className="row mb-4 align-items-center">
            <div className="col-md-6">
              <h1 className="display-9 fw-bold mb-3 text-gradient mt-3">
                Code quality report
              </h1>
              {/* Show project info when no specific commit is selected */}
              {projectId && (
                <div className="d-flex flex-column gap-1">
                  <small className="text-muted fs-6">
                    <strong>Project name:</strong> <span>{projectName}</span>  <strong className="header-small">Project ID:</strong> {projectId}
                    <span className="header-small">
                      <strong>Commit:</strong> {(selectedCommitId || commit_id_local)?.substring(0, 4) || 'N/A'}...
                      {(selectedCommitId || commit_id_local) && (
                        <button
                          className="btn btn-link btn-sm p-0 ms-1"
                          onClick={() => copyToClipboard(selectedCommitId || commit_id_local)}
                          title="Copy full commit ID"
                          style={{
                            verticalAlign: 'baseline', color: 'black', position: 'relative', bottom: '3px'
                          }}
                        >
                          <Copy size={16} />
                          {showCopyTooltip && (
                            <span className="ms-2">
                              Copied!
                            </span>
                          )}
                        </button>
                      )}
                    </span>
                  </small>
                </div>
              )}
            </div>

            <div className="col-md-6 d-flex justify-content-end align-items-center gap-3">
              {/* Efficient Commit Search Component */}
              <EfficientCommitSearch
                projectId={projectId}
                onCommitSelect={handleCommitSelect}
                selectedCommitId={selectedCommitId}
                disabled={loadingCommitData}
              />
            </div>
          </div>

          

          <div className="row mb-4">
            <div className="col-md-12">
              <SmellTypeTabs />
            </div>
          </div>

          <div className="row g-4">
            {selectedSmellTypeFilter === "all" && (
              <>
                <div className="row mt-4">
                  <div className="col-md-6">
                    {pieChartLoading ? (
                      <LoadingSpinner title="Loading smell distribution..." />
                    ) : (
                      <Piechart
                        data={pieChartData}
                        title="Distribution of smell types"
                      />
                    )}
                  </div>

                  <div className="col-md-6">
                    <StatCards
                      summary={analysisSummary}
                      title="Analysis summary"
                    />
                  </div>
                </div>

                {graphData &&
                  graphData.nodes?.length > 0 &&
                  !graphData.error && (
                    <div className="row mt-4">
                      <div className="col-md-12">
                        <WorldCup
                          graph={graphData}
                          title="Dependency graph"
                          headerActions={
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() =>
                                navigate("/project/analyze-dependency", {
                                  state: {
                                    projectId,
                                    selectedCommitId,
                                  },
                                })
                              }
                              style={{ background: "#75C4C0", color: "white", border: "none", padding: "10px 20px", fontSize: "14px" }}
                            >
                              Analyze dependencies
                            </button>
                          }
                        />
                      </div>
                    </div>
                  )}

                <div className="row g-3">
                  <div className="col-md-12">
                    <div className="card p-3 shadow-sm">
                      <h5 className="mb-5">
                        Top 5 files with highest number of code smells
                      </h5>
                      <ul className="nav nav-tabs">
                        {[
                          "all",
                          "implementation",
                          "design",
                          "architecture",
                        ].map((type) => (
                          <li className="nav-item" key={type}>
                            <a
                              className={`nav-link ${selectedType === type ? "active" : ""
                                }`}
                              onClick={() => handleTypeChange(type)}
                              style={{ cursor: "pointer" }}
                            >
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </a>
                          </li>
                        ))}
                      </ul>

                      {barChartLoading ? (
                        <LoadingSpinner title="Loading Top 5 files with highest number of code smells..." />
                      ) : (
                        <Barchart
                          data={filteredData}
                          title="Top files with highest number of code smells"
                          smellType="true"
                          gradientID="grad1"
                          color="#75C4C0"
                          fadeColor="#b8e0de"
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-12">
                    <div className="card p-3 shadow-sm">
                      <h5>Top 5 highest number of code smells</h5>

                      {topSmellTypesLoading ? (
                        <LoadingSpinner title="Loading 5 highest number of code smells" />
                      ) : (
                        <Barchart
                          data={topSmellTypes}
                          title="Top files with highest number of code smells"
                          smellType="true"
                          color="#EA7775"
                          fadeColor="#F38181"
                          gradientID="grad2"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {selectedSmellTypeFilter === "architecture" && (
              <>
                <div className="row g-3">
                  <div className="col-md-6">
                    {architectureDonutData &&
                      architectureDonutData.length > 0 ? (
                      <DonutChart data={architectureDonutData} />
                    ) : (
                      <NoDataAvailable title="Architecture Smell Distribution" />
                    )}
                  </div>
                  <div className="col-md-6">
                    {architectureTableData &&
                      architectureTableData.length > 0 ? (
                      <Table
                        title="Architecture smells"
                        data={architectureTableData}
                        columns={arcTableColumns}
                        searchKey="package"
                      />
                    ) : (
                      <NoDataAvailable title="Architecture smells" />
                    )}
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-12 col-sm-12 col-lg-12 col-md-12">
                    {architectureTreeMapData &&
                      architectureTreeMapData.length > 0 ? (
                      <TreeMapChart
                        data={architectureTreeMapData}
                        title="Smell density by package"
                        variant="architecture"
                      />
                    ) : (
                      <NoDataAvailable title="Smell density by package" />
                    )}
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-md-12">
                    <div className="card p-3 shadow-sm">
                      <h5>Top 5 highest number of code smells</h5>

                      {implementationBarChartLoading ? (
                        <LoadingSpinner title="Loading Implementation Smell bar chart..." />
                      ) : architectureBarData &&
                        architectureBarData.length > 0 ? (
                        <Barchart
                          data={architectureBarData}
                          title="Top 5 highest number of code smells"
                          smellType="true"
                          gradientID="grad3"
                          color="#75C4C0"
                          fadeColor="#b8e0de"
                        />
                      ) : (
                        <NoDataAvailable title="Top 5 highest number of code smells" />
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {selectedSmellTypeFilter === "implementation" && (
              <>
                <div className="row g-3">
                  <div className="col-md-5">
                    {implementationPieChart &&
                      implementationPieChart.length > 0 ? (
                      <Piechart
                        data={implementationPieChart}
                        title="Smell distribution by smell type"
                      />
                    ) : (
                      <NoDataAvailable title="Smell distribution by smell type" />
                    )}
                  </div>

                  <div className="col-md-7">
                    {implementationTableData &&
                      implementationTableData.length > 0 ? (
                      <Table
                        title="Implementation smells"
                        data={implementationTableData}
                        columns={impTableColumns}
                        searchKey="function"
                      />
                    ) : (
                      <NoDataAvailable title="Implementation smells" />
                    )}
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-12">
                    {implementationTreeMapData &&
                      implementationTreeMapData.length > 0 ? (
                      <TreeMapChart
                        data={implementationTreeMapData}
                        title="Smell density by method/function"
                        variant="implementation"
                      />
                    ) : (
                      <NoDataAvailable title="Smell density by method/function" />
                    )}
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-md-12">
                    <div className="card p-3 shadow-sm">
                      <h5>Top files/packages with highest number of code smells</h5>

                      {implementationBarChartLoading ? (
                        <LoadingSpinner title="Loading Implementation Smell bar chart..." />
                      ) : implementationTopSmells &&
                        implementationTopSmells.length > 0 ? (
                        <Barchart
                          data={implementationTopSmells}
                          title="Top files with highest number of code smells"
                          smellType="true"
                          gradientID="grad3"
                          color="#75C4C0"
                          fadeColor="#b8e0de"
                        />
                      ) : (
                        <NoDataAvailable title="Top files with highest number of code smells" />
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {selectedSmellTypeFilter === "design" && (
              <>
                <div className="row g-3">
                  <div className="col-md-5">
                    {designPieChartData && designPieChartData.length > 0 ? (
                      <Piechart
                        data={designPieChartData}
                        title="Smell distribution by smell type"
                      />
                    ) : (
                      <NoDataAvailable title="Smell distribution by smell type" />
                    )}
                  </div>

                  <div className="col-md-7">
                    {designTableData && designTableData.length > 0 ? (
                      <Table
                        title="Design smells"
                        data={designTableData}
                        columns={designTableColumns}
                        searchKey="class"
                      />
                    ) : (
                      <NoDataAvailable title="Design smells" />
                    )}
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-12">
                    {designTreeMapData && designTreeMapData.length > 0 ? (
                      <TreeMapChart
                        data={designTreeMapData}
                        title="Smell density by file or class"
                        variant="design"
                      />
                    ) : (
                      <NoDataAvailable title="Smell density by file or class" />
                    )}
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-12">
                    <div className="card p-3 shadow-sm">
                      <h5>Top 5 files/packages with highest number of code smells</h5>

                      {designBarChartData && designBarChartData.length > 0 ? (
                        <Barchart
                          data={designBarChartData}
                          title="Top files with highest number of code smells"
                          smellType="true"
                          gradientID="grad4"
                          color="#75C4C0"
                          fadeColor="#b8e0de"
                        />
                      ) : (
                        <NoDataAvailable title="Top files with highest number of code smells" />
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {selectedSmellTypeFilter === "testability" && (
              <>
                <div className="col-md-5">
                  {testabilityPieChartData &&
                    testabilityPieChartData.length > 0 ? (
                    <Piechart
                      data={testabilityPieChartData}
                      title="Smell distribution"
                    />
                  ) : (
                    <NoDataAvailable title="Smell distribution" />
                  )}
                </div>

                <div className="col-md-7">
                  {testabilityTableData && testabilityTableData.length > 0 ? (
                    <Table
                      title="Testability smells"
                      data={testabilityTableData}
                      columns={testabilityTableColumns}
                      searchKey="package"
                    />
                  ) : (
                    <NoDataAvailable title="Testability smells" />
                  )}
                </div>

                <div className="row g-3">
                  <div className="col-md-12">
                    {testabilityTreeMapData &&
                      testabilityTreeMapData.length > 0 ? (
                      <TreeMapChart
                        data={testabilityTreeMapData}
                        title="Smell density by file or class"
                        variant="testability"
                      />
                    ) : (
                      <NoDataAvailable title="Smell density by file or class" />
                    )}
                  </div>
                </div>

                <div className="col-md-12">
                  <div className="card p-3 shadow-sm">
                    <h5>Top 5 files/packages with highest number of code smells</h5>

                    {testabilityBarChartData &&
                      testabilityBarChartData.length > 0 ? (
                      <Barchart
                        data={testabilityBarChartData}
                        title="Top packages with highest number of code smells"
                        smellType="true"
                        gradientID="grad6"
                        color="#75C4C0"
                        fadeColor="#b8e0de"
                      />
                    ) : (
                      <NoDataAvailable title="Top packages with highest number of code smells" />
                    )}
                  </div>
                </div>
              </>
            )}

            {selectedSmellTypeFilter === "test" && (
              <>
                <div className="col-md-5">
                  {testPieChartData && testPieChartData.length > 0 ? (
                    <Piechart
                      data={testPieChartData}
                      title="Test smell distribution"
                    />
                  ) : (
                    <NoDataAvailable title="Test smell distribution" />
                  )}
                </div>

                <div className="col-md-7">
                  {testTableData && testTableData.length > 0 ? (
                    <Table
                      title="Test smells"
                      data={testTableData}
                      columns={testTableColumns}
                      searchKey="package"
                    />
                  ) : (
                    <NoDataAvailable title="Test smells" />
                  )}
                </div>

                <div className="row g-3">
                  <div className="col-md-12">
                    {testTreeMapData && testTreeMapData.length > 0 ? (
                      <TreeMapChart
                        data={testTreeMapData}
                        title="Smell density by file or class"
                        variant="test"
                      />
                    ) : (
                      <NoDataAvailable title="Smell density by file or class" />
                    )}
                  </div>
                </div>

                <div className="col-md-12">
                  <div className="card p-3 shadow-sm">
                    <h5>Top 5 packages with highest number of code smells</h5>

                    {testBarChartData && testBarChartData.length > 0 ? (
                      <Barchart
                        data={testBarChartData}
                        title="Top packages with highest number of code smells"
                        smellType="true"
                        gradientID="grad5"
                        color="#75C4C0"
                        fadeColor="#b8e0de"
                      />
                    ) : (
                      <NoDataAvailable title="Top packages with highest number of code smells" />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer sidebarOpen={sidebarOpen} />
    </div>
  );
}

export default VisualizeReport;